import { memo, useContext, useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import type { PieChart } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { formatWithCommas } from '@trinity/chat-components/utils/chart';
import { replaceTemplateValues } from './template-utils';
import { TemplateTooltip } from './TemplateTooltip';
import { theme } from './theme';

type Props = {
  chart: PieChart;
  colors: string[];
  animate?: boolean;
  columnN?: number;
  unit?: string;
  isPrefix?: boolean;
  isInteractive?: boolean;
};

export const NivoPieChart = memo(
  ({ chart, colors, animate = false, columnN, unit, isPrefix, isInteractive = true }: Props) => {
    const [, setOpen] = useContext(LegendCollapseToggleContext);

    const returnedData = chart.data;
    const data = [];

    for (let i = 0; i < returnedData.length; i++) {
      data.push({ ...returnedData[i], color: colors[i] });
    }

    // Pre-compute total so arc label percentages are accurate
    const total = useMemo(() => returnedData.reduce((sum, d) => sum + d.value, 0), [returnedData]);

    return (
      <div style={{ height: '100%', width: '100%', position: 'relative' }}>
        <ResponsivePie
          data={data}
          margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
          innerRadius={0.5}
          padAngle={0.7}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          colors={colors}
          isInteractive={isInteractive}
          borderWidth={1}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]],
          }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#333333"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLabelsSkipAngle={10}
          arcLinkLabelsStraightLength={24}
          arcLabelsTextColor="#FFFFFF"
          arcLabel={(d) => {
            const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : '0.0';
            return `${pct}%`;
          }}
          tooltip={
            isInteractive
              ? (e) => {
                  const { datum } = e;
                  const pct = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                  // If a tooltip template is provided, use it
                  if (chart.tooltipTemplate) {
                    const sliceExtra = chart.sliceData?.[String(datum.id)] ?? {};
                    const templateValues = {
                      label: datum.label,
                      value: datum.value,
                      percentage: `${pct}%`,
                      id: datum.id,
                      xAxisTitle: chart.xAxisTitle,
                      yAxisTitle: chart.yAxisTitle,
                      unit: unit || '',
                      ...sliceExtra,
                    };
                    const content = replaceTemplateValues(chart.tooltipTemplate, templateValues);
                    return <TemplateTooltip content={content} />;
                  }
                  // Otherwise use the default tooltip with percentage
                  return (
                    <PieHoverText
                      name={datum.label}
                      count={datum.value}
                      percentage={pct}
                      columnN={columnN}
                      unit={unit}
                      isPrefix={isPrefix}
                    />
                  );
                }
              : undefined
          }
          onClick={
            isInteractive
              ? (data) => {
                  setOpen(true);
                }
              : undefined
          }
          animate={animate}
          theme={theme}
        />
      </div>
    );
  }
);

// ─── Pie-specific tooltip (shows % alongside comma-formatted value) ────────────

type PieHoverTextProps = {
  name: string | number;
  count: number;
  percentage: string;
  columnN?: number;
  unit?: string;
  isPrefix?: boolean;
};

function PieHoverText({
  name,
  count,
  percentage,
  columnN,
  unit,
  isPrefix,
}: PieHoverTextProps): React.ReactElement {
  const formatted = formatWithCommas(count);
  const formattedValue = unit ? (isPrefix ? `${unit}${formatted}` : `${formatted}${unit}`) : formatted;

  return (
    <PieTooltipRoot>
      <PieTooltipHeader>{name}</PieTooltipHeader>
      <PieTooltipRow>
        <span>
          {unit ? 'Value' : 'Count'}: {formattedValue}
        </span>
        <PieTooltipPct>{percentage}%</PieTooltipPct>
      </PieTooltipRow>
      {columnN !== undefined && (
        <PieTooltipRow>
          <span>Column n: {columnN}</span>
        </PieTooltipRow>
      )}
    </PieTooltipRoot>
  );
}

import styled from '@emotion/styled';
import type React from 'react';

const PieTooltipRoot = styled.div`
  background: white;
  padding: 9px 12px;
  border: 1px solid #ccc;
  min-width: 180px;
`;

const PieTooltipHeader = styled.div(({ theme }: { theme?: { fonts?: { semiBold?: string } } }) => ({
  fontFamily: theme?.fonts?.semiBold ?? 'inherit',
  fontWeight: 600,
  marginBottom: '4px',
}));

const PieTooltipRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const PieTooltipPct = styled.span`
  font-weight: 700;
  color: #555;
`;
